import React, { useEffect, useState } from 'react';
import CertifiedTutors from '../components/CertifiedTutors';
import GuideModal from '../components/GuideModal';
import './QuranMemorizationCourse.css';
import ConsultationSection from '../components/ConsultationSection';

const QuranMemorizationCourse = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [faqOpenStates, setFaqOpenStates] = useState({
    'hifz-left-0': true
  });

  const tabsData = [
    {
      label: 'Structured Hifz Curriculum',
      title: 'Structured Hifz Curriculum',
      content: (<>Our Quran memorization course follows a proven, step-by-step curriculum. Students begin with <a style={{ color: "#006100" }} href="/online-tajweed-classes-tajweed-course/">Tajweed</a> correction to ensure every verse is memorized with proper pronunciation and Makhraj. From short Surahs in Juz Amma to longer chapters, the progression is gradual, systematic, and sustainable.
      </>)
    },
    {
      label: 'Daily New Lesson + Revision Plan',
      title: 'Daily New Lesson + Revision Plan',
      content: 'Every session includes recitation of the new lesson (Sabaq) as well as revision of recently memorized portions (Sabqi) and older memorized portions (Manzil). This triple-layer revision system ensures that what students memorize stays memorized permanently.'
    },
    {
      label: 'One-on-One Classes',
      title: 'One-on-One Classes with Certified Huffaz',
      content: 'Every online Quran memorization class is a private, live session with a dedicated Hafiz or Hafiza. This is not a group class. Your child or you receive full, undivided attention, immediate correction, and personalized guidance in every session.'
    },
    {
      label: 'Flexible Scheduling',
      title: 'Flexible Scheduling - 7 Days a Week',
      content: 'We understand that families in the USA, UK, and Canada have busy routines. Our online Quran hifz classes are available every day of the week, including weekends, with time slots across all major time zones. You choose when you want to learn.'
    },
    {
      label: 'Regular Progress Evaluation',
      title: 'Regular Progress Evaluation',
      content: 'Students are evaluated weekly to track Hifz progress. Parents receive regular reports on their child’s memorization, Tajweed quality, and revision status, keeping the entire family involved in the journey.'
    },
    {
      label: 'Memorization Classes for Sisters',
      title: 'Online Quran Memorization Classes for Sisters',
      content: 'We offer dedicated online Quran memorization classes for sisters taught exclusively by qualified female teachers (Hafizas). Sisters can learn in a fully Islamic, comfortable, and private environment without any concern about mixed-gender classes.'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setRotation(scrollY * 0.15);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="memorization-course-page">
      {/* Hero Section */}
      <section
        className="about-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.51), rgba(0, 0, 0, 0.47)), url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <div className="about-hero__content">
          <h1 className="about-hero__title">Online Quran Memorization Classes, Memorize Quran Online with Expert Huffaz</h1>

          <p className="about-hero__desc">
            Start your Hifz journey from the comfort of your home. Join Al Quran Lessons’ structured online Quran memorization classes guided by certified male and female Huffaz, available for kids, adults, and sisters worldwide. Our Quran memorization course helps you to memorize the Quran online, whether you want to memorize some Surahs or all of the Quran. In the Islamic faith, “to memorize the Quran online” is also called “Hifz,” which has a very big significance in Islamic history. Get a free trial now and start memorizing the Holy Quran online.
          </p>

          <nav className="about-hero__breadcrumb" aria-label="breadcrumb">
            <a href="/" className="about-hero__breadcrumb-link">
              Home
            </a>
            <span className="about-hero__breadcrumb-separator">&gt;</span>
            <span className="about-hero__breadcrumb-current">Quran Memorization Course</span>
          </nav>
        </div>
      </section>

      {/* Main Details Section */}
      <section
        className="memorization-details"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/hero-bottom.webp')`
        }}
      >
        <div className="memorization-details__container">

          {/* Left Column: Text & Content */}
          <div className="memorization-details__content">
            <h2 className="memorization-details__title">
              Our Specially Designed <span className="highlight-green">Online Quran Memorization Course</span> for Everyone!
            </h2>

            <p className="memorization-details__paragraph">
              At <a style={{ color: "#006100" }} href="/">Al Quran Lesson</a>, we offer the best online Quran memorization classes designed for Muslims of all ages, children, adults, brothers, and sisters who want to memorize the Holy Quran under proper guidance and Tajweed. Whether your goal is to memorize a few Surahs, a Juz, or complete the entire Quran, our Quran memorization course is structured to fit your pace, schedule, and level of prior Quranic knowledge.
            </p>

            <p className="memorization-details__paragraph">
              Our <a style={{ color: "#006100" }} href="/our-tutors/">certified Quran teachers</a> have years of experience teaching Quran memorization online to students in the USA. Every class is conducted live, one-on-one via video, ensuring maximum attention, personalized feedback, and consistent progress. With Al Quran Lesson, Hifz is no longer limited by geography or time; you can start Quran memorization online from wherever you are, at a time that works for you.
            </p>

            <ul className="memorization-details__list">
              <li className="memorization-details__list-item">
                <span className="memorization-details__list-bullet"></span>
                Certified native Arabic tutors
              </li>
              <li className="memorization-details__list-item">
                <span className="memorization-details__list-bullet"></span>
                24/7 Availability, all US time zones
              </li>
              <li className="memorization-details__list-item">
                <span className="memorization-details__list-bullet"></span>
                Completion certificate for every course
              </li>
            </ul>

            <div className="memorization-details__contacts">
              <a href="tel:+17164577657" className="memorization-details__contact-item">
                <div className="memorization-details__contact-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-details__contact-svg">
                    <path d="M6.62 10.79a15.15 15.15 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27 11.72 11.72 0 003.7.59 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.72 11.72 0 00.59 3.7 1 1 0 01-.27 1.11z" />
                  </svg>
                </div>
                <div className="memorization-details__contact-text">
                  <span className="memorization-details__contact-label">Call Us Anytime</span>
                  <span className="memorization-details__contact-value">(716) 457-7657</span>
                </div>
              </a>

              <a href="mailto:info@alquranlesson.com" className="memorization-details__contact-item">
                <div className="memorization-details__contact-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-details__contact-svg">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </div>
                <div className="memorization-details__contact-text">
                  <span className="memorization-details__contact-label">Email Us Anytime</span>
                  <span className="memorization-details__contact-value">info@alquranlesson.com</span>
                </div>
              </a>
            </div>

            <a href="/free-trial/" className="memorization-details__btn">
              Book Your 3-Day Free Trial
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-details__btn-icon">
                <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4v3z" />
              </svg>
            </a>
          </div>

          {/* Right Column: Overlapping Images */}
          <div className="memorization-details__images-wrapper">
            {/* Rotating Stamp */}
            <div
              className="memorization-details__stamp"
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/G-3.webp`}
                alt="Certified Quran Academy Stamp"
                className="memorization-details__stamp-img"
              />
            </div>

            {/* Top Image */}
            <div className="memorization-details__img-container memorization-details__img-container--top">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/BG-3.jpg`}
                alt="Online Quran Classes For Kids"
                className="memorization-details__main-img"
              />
            </div>

            {/* Bottom Image */}
            <div className="memorization-details__img-container memorization-details__img-container--bottom">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/wmremove-transformed-2.jpeg`}
                alt="Online Quran Classes For Kids"
                className="memorization-details__main-img"
              />
            </div>
          </div>

        </div>
      </section>

      {/* 4 Cards Section */}
      <section className="memorization-cards-section">
        <div className="memorization-cards__container">
          <div className="memorization-card">
            <h3 className="memorization-card__title">Easy Memorization</h3>
            <p className="memorization-card__desc">
              The Huffaz divide the Surahs into a particular number of verses to make memorization easy
            </p>
          </div>
          <div className="memorization-card">
            <h3 className="memorization-card__title">24/7 Support</h3>
            <p className="memorization-card__desc">
              We offer 24/7 support to our students to ensure that they have access to assistance whenever they need it.
            </p>
          </div>
          <div className="memorization-card">
            <h3 className="memorization-card__title">Lesson Revision</h3>
            <p className="memorization-card__desc">
              Students are given a plan to follow pertaining to lesson revision and practice
            </p>
          </div>
          <div className="memorization-card">
            <h3 className="memorization-card__title">New Lesson</h3>
            <p className="memorization-card__desc">
              Every day students take new lesson in addition to reciting the old lesson to the Huffaz
            </p>
          </div>
        </div>
      </section>

      {/* Why Memorize Quran Section */}
      <section
        className="memorization-why-memorize"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/Hero-4.jpg')`
        }}
      >
        <div className="memorization-why-memorize__container">
          {/* Left Column: Text Content */}
          <div className="memorization-why-memorize__content">
            <h2 className="memorization-why-memorize__title">
              Why Should You <span className="highlight-green">Memorize The Holy Quran?</span>
            </h2>

            <p className="memorization-why-memorize__quote">
              Allah (SWT) says in the Quran: <em>“And We have certainly made the Qur’an easy for remembrance, so is there any who will remember?”</em> (Surah Al-Qamar, 54:17)
            </p>

            <p className="memorization-why-memorize__paragraph">
              Becoming a Hafiz is one of the greatest honors in Islam. The rewards for memorizing the Quran are immense, from intercession on the Day of Judgment for your family to elevation in ranks in Jannah. But beyond the spiritual reward, Quran memorization:
            </p>

            <ul className="memorization-why-memorize__list">
              <li>
                <strong>Strengthens your connection with Allah (SWT)</strong> through daily recitation and reflection.
              </li>
              <li>
                <strong>Improves cognitive abilities</strong>, memory, focus, and discipline in students of all ages.
              </li>
              <li>
                <strong>Builds a lifelong Ibadah habit</strong> that enriches every Salah with memorized verses.
              </li>
              <li>
                <strong>Earns your family honor</strong>, the Hafiz is given a crown on the Day of Judgment, as mentioned in the Hadith.
              </li>
              <li>
                <strong>Instills confidence and identity</strong> in Muslim children growing up in non-Muslim countries.
              </li>
            </ul>

            <p className="memorization-why-memorize__paragraph">
              This is why thousands of families trust Al Quran Lesson to help them memorize Quran online, because Hifz is not just a course, it is a life-changing journey.
            </p>

            <a href="/free-trial/" className="memorization-why-memorize__btn">
              Get Free Trial For Classes
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-why-memorize__btn-icon">
                <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4v3z" />
              </svg>
            </a>
          </div>

          {/* Right Column: Image with border */}
          <div className="memorization-why-memorize__image-col">
            <div className="memorization-why-memorize__image-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/g-5.jpg`}
                alt="Boy studying Quran on laptop"
                className="memorization-why-memorize__img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Tabbed Section */}
      <section
        className="memorization-whats-included-tabs"
        style={{
          '--bg-url': `url('${process.env.PUBLIC_URL}/assets/images/green-bg.png')`
        }}
      >
        <div className="memorization-whats-included-tabs__container">
          <h2 className="memorization-whats-included-tabs__title">
            Our Quran Memorization Course What's Included
          </h2>
          <p className="memorization-whats-included-tabs__subtitle">
            Our Hifz Quran Online Program Is A Complete, Structured Course That Goes Beyond Just Memorization. Here's What Every Student Gets:
          </p>

          {/* Tabs Control Row */}
          <div className="memorization-whats-included-tabs__control-container">
            <div className="memorization-whats-included-tabs__row">
              {tabsData.slice(0, 3).map((tab, index) => (
                <button
                  key={index}
                  className={`memorization-whats-included-tabs__btn ${activeTab === index ? 'memorization-whats-included-tabs__btn--active' : ''}`}
                  onClick={() => setActiveTab(index)}
                >
                  <span>{tab.label}</span>
                  {activeTab === index && (
                    <svg className="memorization-whats-included-tabs__arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.22 5.03a.75.75 0 111.06-1.06l5.5 5.5a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 11-1.06-1.06l4.168-4.17H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            <div className="memorization-whats-included-tabs__row">
              {tabsData.slice(3, 6).map((tab, idx) => {
                const index = idx + 3;
                return (
                  <button
                    key={index}
                    className={`memorization-whats-included-tabs__btn ${activeTab === index ? 'memorization-whats-included-tabs__btn--active' : ''}`}
                    onClick={() => setActiveTab(index)}
                  >
                    <span>{tab.label}</span>
                    {activeTab === index && (
                      <svg className="memorization-whats-included-tabs__arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.22 5.03a.75.75 0 111.06-1.06l5.5 5.5a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 11-1.06-1.06l4.168-4.17H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Display Card */}
          <div className="memorization-whats-included-tabs__content-box">
            <h3 className="memorization-whats-included-tabs__content-title">
              {tabsData[activeTab].title}
            </h3>
            <p className="memorization-whats-included-tabs__content-text">
              {tabsData[activeTab].content}
            </p>
          </div>
        </div>
      </section>

      {/* Who Can Join Section */}
      <section
        className="memorization-who-can-join"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/Hero-4.jpg')`
        }}
      >
        <div className="memorization-who-can-join__container">
          {/* Left Column: Text Content */}
          <div className="memorization-who-can-join__content">
            <h2 className="memorization-who-can-join__title">
              Who Can Join Our <span className="highlight-green">Online Quran Hifz Classes?</span>
            </h2>

            <p className="memorization-who-can-join__paragraph">
              Our Quran hifz online program is open to students of all backgrounds and skill levels. You are the right candidate if:
            </p>

            <ul className="memorization-who-can-join__list">
              <li>
                You are a beginner who can read the Quran but wants to begin memorization
              </li>
              <li>
                You are a parent looking for the best online Quran memorization classes for your child
              </li>
              <li>
                You are an adult who wants to memorize the Quran at your own pace
              </li>
              <li>
                You are a sister looking for female-only Quran memorization instruction
              </li>
              <li>
                You are someone who has already memorized some portions and wants to resume Hifz
              </li>
              <li>
                You live in the USA, UK, Canada, or anywhere in the world, and cannot access a local Hifz school
              </li>
            </ul>

            <p className="memorization-who-can-join__paragraph">
              There is no age limit. We teach children as young as 5 and adults in their 40s and beyond. The journey of Hifz is for everyone who has the intention and commitment.
            </p>

            <a href="/free-trial/" className="memorization-who-can-join__btn">
              Get Free Trial For Classes
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-who-can-join__btn-icon">
                <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4v3z" />
              </svg>
            </a>
          </div>

          {/* Right Column: Image with border */}
          <div className="memorization-who-can-join__image-col">
            <div className="memorization-who-can-join__image-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/g-5.jpg`}
                alt="Who can join Quran Hifz classes"
                className="memorization-who-can-join__img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Certified Tutors Section */}
      <CertifiedTutors />

      {/* Curriculum Section */}
      <section className="memorization-curriculum">
        <div className="memorization-curriculum__container">
          <h2 className="memorization-curriculum__title">
            Our Quran Memorization Course Curriculum
          </h2>
          <div className="memorization-curriculum__divider"></div>

          <p className="memorization-curriculum__intro">
            Our Quran memorization course is divided into structured levels to guide students from their very first verse to the completion of all 30 Juz:
          </p>

          <div className="memorization-curriculum__levels">
            <p className="memorization-curriculum__level">
              <strong className="memorization-curriculum__level-label">Level 1 — Foundation (Tajweed & Short Surahs)</strong> Before memorization begins, we correct your Tajweed and Makharij. Students start with Juz Amma (the 30th Juz), which is the most commonly memorized section and ideal for building momentum and confidence.
            </p>
            <p className="memorization-curriculum__level">
              <strong className="memorization-curriculum__level-label">Level 2 — Intermediate (Juz 1–10)</strong> Once the foundation is strong, students begin memorizing from the beginning of the Quran. Lessons are assigned in manageable portions with daily revision integrated into every session.
            </p>
            <p className="memorization-curriculum__level">
              <strong className="memorization-curriculum__level-label">Level 3 — Advanced (Juz 11–29)</strong> Students continue with longer and more complex Surahs. The revision cycle becomes more intensive as the amount of memorized content grows. Teachers closely monitor the quality and retention of previously memorized portions.
            </p>
            <p className="memorization-curriculum__level">
              <strong className="memorization-curriculum__level-label">Level 4 — Completion & Ijazah Preparation</strong> Students complete the 30th Juz and consolidate the full Quran in memory. Upon successful completion, students receive a Certificate of Hifz from Al Quran Lesson as a testament to their achievement.
            </p>
          </div>
        </div>
      </section>

      {/* Guide Banner Section */}
      <section className="memorization-guide-banner">
        <div className="memorization-guide-banner__container">
          <h2 className="memorization-guide-banner__title">
            An Expert Guide To Online Quran Memorization (Hifz)
          </h2>
          <p className="memorization-guide-banner__subtitle">
            A comprehensive guide to <em>online Hifz of the Quran</em>, based on expert guidance.
          </p>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="memorization-guide-banner__btn"
            style={{ cursor: 'pointer', border: 'none' }}
          >
            READ GUIDE
          </button>
        </div>
      </section>

      {/* Benefits Header Section */}
      <section
        className="memorization-benefits-header"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/bg-mosque.avif')`
        }}
      >
        <div className="memorization-benefits-header__container">
          <div className="memorization-benefits-header__badge">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-benefits-header__badge-icon">
              <path d="M12 2C11.5 2 11 3 11 4v1.07C7.61 5.57 5 8.48 5 12v6H3v2h18v-2h-2v-6c0-3.52-2.61-6.43-6-6.93V4c0-1-.5-2-1-2zm0 14a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
            <span className="memorization-benefits-header__badge-text">Benefits</span>
          </div>

          <h2 className="memorization-benefits-header__title">
            Why Al Quran Lesson's Online Quran Memorization Classes are the Best For Every Age?
          </h2>

          <p className="memorization-benefits-header__subtitle">
            Hundreds of families across the United States have chosen Al Quran Lesson for their Quran hifz online journey. Here's why:
          </p>
        </div>
      </section>

      {/* Benefits Grid Section */}
      <section className="about-features" id="memorization-benefits-features" style={{ paddingBottom: '80px' }}>
        <div className="about-features__container" style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div className="about-features__grid">

            {/* Left Column: Features 1-3 */}
            <div className="about-features__col about-features__col--left">
              {/* Feature 01 */}
              <div className="about-features__item about-features__item--left">
                <div className="about-features__text">
                  <h3 className="about-features__item-title">3-Day Free Trial</h3>
                  <p className="about-features__item-desc">
                    Try our online Quran memorization classes completely free for 3 days, no payment, no credit card, no commitment. Just real live sessions with a certified tutor before you decide.
                  </p>
                </div>
                <div className="about-features__badge">01</div>
              </div>

              {/* Feature 02 */}
              <div className="about-features__item about-features__item--left">
                <div className="about-features__text">
                  <h3 className="about-features__item-title">Personalized Sessions</h3>
                  <p className="about-features__item-desc">
                    Your Pace, Your Plan. Your tutor builds a custom learning roadmap based on your current level and goals, ensuring every lesson moves you forward without rushing or falling behind.
                  </p>
                </div>
                <div className="about-features__badge">02</div>
              </div>

              {/* Feature 03 */}
              <div className="about-features__item about-features__item--left">
                <div className="about-features__text">
                  <h3 className="about-features__item-title">Separate Quran Memorization Classes for Sisters</h3>
                  <p className="about-features__item-desc">
                    Female students are taught exclusively by our certified female Qarias in a private, comfortable, and fully modest learning environment, no exceptions.
                  </p>
                </div>
                <div className="about-features__badge">03</div>
              </div>
            </div>

            {/* Middle Column: Dome/Arch Capsule Image */}
            <div className="about-features__image-col">
              <div className="about-features__image-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/About.avif`}
                  alt="Man praying inside mosque archway"
                  className="about-features__img"
                />
              </div>
            </div>

            {/* Right Column: Features 4-6 */}
            <div className="about-features__col about-features__col--right">
              {/* Feature 04 */}
              <div className="about-features__item about-features__item--right">
                <div className="about-features__badge">04</div>
                <div className="about-features__text">
                  <h3 className="about-features__item-title">Live One-on-One Classes</h3>
                  <p className="about-features__item-desc">
                    Every session is a live, interactive class with your dedicated tutor, not a pre-recorded video, so your pronunciation gets corrected in real time, every lesson.
                  </p>
                </div>
              </div>

              {/* Feature 05 */}
              <div className="about-features__item about-features__item--right">
                <div className="about-features__badge">05</div>
                <div className="about-features__text">
                  <h3 className="about-features__item-title">Hafiz & Ijazah-Certified Teachers</h3>
                  <p className="about-features__item-desc">
                    Our tutors are Huffaz and Ijazah holders, meaning they carry an unbroken chain of Quranic authority, so you memorize the way it has always been authentically transmitted.
                  </p>
                </div>
              </div>

              {/* Feature 06 */}
              <div className="about-features__item about-features__item--right">
                <div className="about-features__badge">06</div>
                <div className="about-features__text">
                  <h3 className="about-features__item-title">Flexible Scheduling</h3>
                  <p className="about-features__item-desc">
                    Choose any days and times that fit your routine, morning, evening, or weekend with full availability across all USA time zones around the clock.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Centered CTA Button */}
          <div className="about-features__btn-wrapper" style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <a href="/free-trial/" className="memorization-why-memorize__btn" style={{ margin: '0' }}>
              Get Free Trial For Classes
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="memorization-why-memorize__btn-icon">
                <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4v3z" />
              </svg>
            </a>
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <section
        className="memorization-testimonials"
        style={{
          '--bg-url': `url('${process.env.PUBLIC_URL}/assets/images/green-bg.png')`
        }}
      >
        <div className="memorization-testimonials__container">
          <div className="memorization-testimonials__badge">Real Students, Real Results</div>
          <h2 className="memorization-testimonials__title">Their Stories Speak For Us</h2>
          <p className="memorization-testimonials__subtitle">
            Real Students, Real Progress. Every Story Below Is From A Verified Student Of Al Quran Lesson.
          </p>

          <div className="memorization-testimonials__grid">
            {/* Card 1 */}
            <div className="memorization-testimonials__card">
              <div className="memorization-testimonials__quote-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.19 14.75c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83C6.73 9.08 5.75 10.75 5.62 12c1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0zm11 0c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83c-2.73 1.25-3.71 2.92-3.84 4.17 1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0z" />
                </svg>
              </div>
              <p className="memorization-testimonials__text">
                My daughter struggled to memorize even short Surahs before joining the Al Quran Lesson. After just 4 months with her Hafiza, she has memorized the entire Juz Amma with correct Tajweed. I am very happy with the results.
              </p>
              <div className="memorization-testimonials__divider"></div>
              <div className="memorization-testimonials__author">
                <div className="memorization-testimonials__author-meta">
                  <span className="memorization-testimonials__author-name">Amina</span>
                  <span className="memorization-testimonials__author-info">United States - Parent</span>
                </div>
                <div className="memorization-testimonials__duration-badge">2 Weeks</div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="memorization-testimonials__card">
              <div className="memorization-testimonials__quote-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.19 14.75c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83C6.73 9.08 5.75 10.75 5.62 12c1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0zm11 0c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83c-2.73 1.25-3.71 2.92-3.84 4.17 1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0z" />
                </svg>
              </div>
              <p className="memorization-testimonials__text">
                I started the Hifz course as a complete beginner at age 35, worried it was too late. My teacher was incredibly patient and structured my memorization plan perfectly. Within 6 months, I completed my first Juz with proper Tajweed. Alhamdulillah.
              </p>
              <div className="memorization-testimonials__divider"></div>
              <div className="memorization-testimonials__author">
                <div className="memorization-testimonials__author-meta">
                  <span className="memorization-testimonials__author-name">Nabeel.</span>
                  <span className="memorization-testimonials__author-info">United States - Student</span>
                </div>
                <div className="memorization-testimonials__duration-badge">3 Weeks</div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="memorization-testimonials__card">
              <div className="memorization-testimonials__quote-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.19 14.75c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83C6.73 9.08 5.75 10.75 5.62 12c1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0zm11 0c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75c0-3.6 3.19-7.16 6.84-8.75l.93 1.83c-2.73 1.25-3.71 2.92-3.84 4.17 1.78.11 3.25 1.5 3.25 3.25 0 .28-.03.56-.09.83.68-.45 1.44-1.07 1.93-1.63l.48.3c.01 0 .01 0 0 0z" />
                </svg>
              </div>
              <p className="memorization-testimonials__text">
                Both my sons are enrolled in the Quran memorization classes, and the female Hafiza option was a blessing for my daughter, too. In 8 months, they have never missed a single class. The flexible scheduling and dedicated teachers make this the best online Quran memorization program we've found. Highly recommend.
              </p>
              <div className="memorization-testimonials__divider"></div>
              <div className="memorization-testimonials__author">
                <div className="memorization-testimonials__author-meta">
                  <span className="memorization-testimonials__author-name">Aiman</span>
                  <span className="memorization-testimonials__author-info">United States - Parent</span>
                </div>
                <div className="memorization-testimonials__duration-badge">3 Weeks</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="memorization-faq" style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#f7f9f6' }}>
        <div className="faq-section__container" style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div className="faq-section__grid">

            {/* Left Column */}
            <div className="faq-section__col faq-section__col--left">
              <h2 className="faq-section__title" style={{ fontSize: '36px', textAlign: 'left', color: '#111111', fontWeight: '800', marginBottom: '40px', lineHeight: '1.25' }}>
                Frequently Asked Questions About Our Online Quran Memorization Classes
              </h2>

              <div className="faq-section__accordion">
                {[
                  {
                    id: 'hifz-left-0',
                    question: "How do I get started with online Quran memorization classes?",
                    answer: "Simply sign up for our 3-day free trial. We’ll match you with a qualified Hafiz or Hafiza based on your level, gender preference, and schedule, no credit card, no payment required to begin."
                  },
                  {
                    id: 'hifz-left-1',
                    question: "Does my child need to know how to read Quran before starting?",
                    answer: (<>
                      Yes, basic Quran reading ability is required before beginning Hifz. If your child cannot read yet, we recommend starting with our <a style={{ color: "#006100", textDecoration: "underline" }} href="/noorani-qaida-course/">Noorani Qaida</a> or <a style={{ color: "#006100", textDecoration: "underline" }} href="/quran-reading-classes-online-for-kids-and-adults/">Quran Reading</a> course first.
                    </>)
                  },
                  {
                    id: 'hifz-left-2',
                    question: "How long will it take to memorize the complete Quran?",
                    answer: "It depends on the student’s age, daily practice time, and consistency. On average, students who practice 45–60 minutes daily complete full Hifz in 3 to 5 years. Memorizing individual Surahs or a single Juz takes significantly less time."
                  }
                ].map((faq) => (
                  <div key={faq.id} className={`faq-item ${faqOpenStates[faq.id] ? 'faq-item--open' : ''}`}>
                    <button
                      className="faq-item__header"
                      onClick={() => setFaqOpenStates({ [faq.id]: !faqOpenStates[faq.id] })}
                      aria-expanded={faqOpenStates[faq.id]}
                    >
                      <span>{faq.question}</span>
                      <svg className="faq-item__icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <div className="faq-item__body">
                      <div className="faq-item__content">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column */}
            <div className="faq-section__col faq-section__col--right">
              <div className="faq-section__accordion">
                {[
                  {
                    id: 'hifz-right-0',
                    question: "What age is best to start Hifz?",
                    answer: "There is no wrong age. Children between 6–12 years typically memorize fastest, but we have adult students completing Hifz in their 30s and 40s. The best time to start is now."
                  },
                  {
                    id: 'hifz-right-1',
                    question: "Are there separate classes for sisters and girls?",
                    answer: "Yes. We have dedicated female Huffaz who teach sisters and girls in a completely private, women-only environment that is fully aligned with Islamic values."
                  },
                  {
                    id: 'hifz-right-2',
                    question: "What if my child misses a class?",
                    answer: "No problem. We offer flexible rescheduling. Simply inform us in advance, and we’ll arrange a makeup class at a convenient time. Our support team is available 24/7."
                  },
                  {
                    id: 'hifz-right-3',
                    question: "Will my child also learn Tajweed during Hifz?",
                    answer: "Absolutely. Tajweed correction is built into every memorization class. Students memorize every verse with proper pronunciation and Makharij from day one, not as an add-on, but as a core part of the course."
                  },
                  {
                    id: 'hifz-right-4',
                    question: "How much do the Quran memorization classes cost?",
                    answer: "We offer flexible, affordable fee packages to suit different budgets. Visit our Fee Packages page for full details, or contact us directly and we’ll recommend the best plan for your child’s needs."
                  },
                  {
                    id: 'hifz-right-5',
                    question: "What technology do I need to join online Quran classes?",
                    answer: "You need a smartphone, tablet, or computer with internet, microphone, and camera."
                  }
                ].map((faq) => (
                  <div key={faq.id} className={`faq-item ${faqOpenStates[faq.id] ? 'faq-item--open' : ''}`}>
                    <button
                      className="faq-item__header"
                      onClick={() => setFaqOpenStates({ [faq.id]: !faqOpenStates[faq.id] })}
                      aria-expanded={faqOpenStates[faq.id]}
                    >
                      <span>{faq.question}</span>
                      <svg className="faq-item__icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <div className="faq-item__body">
                      <div className="faq-item__content">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      <ConsultationSection />

      {/* Guide Modal Popup */}
      <GuideModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default QuranMemorizationCourse;

